import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSeriesTranslateRules } from '../bookDetail/BookDetailPage.api';

export type SeriesGlossaryStatus = 'loading' | 'loaded' | 'error' | 'no-series';
type Rules = { alwaysTranslate: Record<string, string[]>; neverTranslate: Record<string, string[]> };
const empty = (): Rules => ({ alwaysTranslate: {}, neverTranslate: {} });

/** Never let a response from another book/owner/series become current. */
export function useSeriesGlossary(ownerId: string, bookId: string, seriesId: string | null | undefined, open: boolean) {
  const key = JSON.stringify([ownerId, bookId, seriesId]);
  const [state, setState] = useState<{ key: string; rules: Rules; status: SeriesGlossaryStatus }>({ key, rules: empty(), status: 'loading' });
  const sequence = useRef(0);
  const lastRefresh = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    if (!seriesId || !ownerId || !bookId) {
      setState({ key, rules: empty(), status: 'no-series' });
      return;
    }
    lastRefresh.current = Date.now();
    setState(previous => ({ key, rules: previous.key === key ? previous.rules : empty(), status: 'loading' }));
    try {
      const rules = await fetchSeriesTranslateRules({ seriesId, userId: ownerId });
      if (request === sequence.current) setState({ key, rules, status: 'loaded' });
    } catch {
      if (request === sequence.current) {
        setState(previous => ({ key, rules: previous.key === key ? previous.rules : empty(), status: 'error' }));
      }
    }
  }, [key, seriesId, ownerId, bookId]);

  useEffect(() => {
    void refresh();
    return () => { sequence.current += 1; };
  }, [refresh, open]);

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'hidden' && Date.now() - lastRefresh.current > 15000) void refresh();
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    const timer = open ? window.setInterval(onReturn, 30000) : undefined;
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [refresh, open]);

  const setRules = useCallback<React.Dispatch<React.SetStateAction<Rules>>>(update => {
    setState(previous => ({ key, status: 'loading', rules: typeof update === 'function' ? update(previous.key === key ? previous.rules : empty()) : update }));
    void refresh();
  }, [key, refresh]);
  return {
    rules: state.key === key ? state.rules : empty(),
    status: state.key === key ? state.status : (!seriesId ? 'no-series' : 'loading') as SeriesGlossaryStatus,
    refresh, setRules,
  };
}
